import { useCallback, useState } from 'react';
import { last } from '../util/helpers';
import { MultiSheetProps } from './MultiSheet';
import { SheetProps } from './Sheet';

export type UseMultiSheetPropType = Omit<MultiSheetProps, 'open' | 'currentLevel'> & { onApply: SheetProps['onApply'] };
export type SheetConfig = {
    title?: string;
    level?: number;
    footer?: boolean;
}

export type SheetState = { level: number, id: string };

export type UseMultiSheetReturnType = MultiSheetProps &
{
    sheetId: string;
    setOpen: (v: boolean) => void;
    previousLevel: (v: number) => void;
    onApplySheet: UseMultiSheetPropType['onApply'];
    navigate: (id: string, level: number) => void;
    history: SheetState[];
    closeModal: (e: any) => void;
};

const useMultiSheet = (props: UseMultiSheetPropType): UseMultiSheetReturnType => {
    const { maxLevel, developmentUrl, onApply, onClose } = props;
    const [sheetId, setSheetId] = useState('default');
    const [msOpen, setMSOpen] = useState(false);
    const [level, setLevel] = useState(1);
    const [sheetHistory, setNavigationHistory] = useState<SheetState[]>([{ level: 1, id: 'default' }]);

    const closeMultiSheetModal = () => {
        setMSOpen(false);
        setLevel(1);
    };

    const openSheet = (id: string, level: number) => {
        setSheetId(id);
        setLevel(level);
        setNavigationHistory(history => [...history, { id, level }]);
    }

    const changeLevel = useCallback((level: number) => {
        if (level <= maxLevel && level > 0) {
            setLevel(level);
            return true;
        }

        return false;
    }, [maxLevel]);


    const goBack = () => {
        const nh = [...sheetHistory];
        nh.pop();
        setNavigationHistory(nh)
        const ps = last(nh);

        if (ps) {
            changeLevel(ps.level);
            setSheetId(ps.id);
        }
    }

    const resetMultiSheet = () => {
        setSheetId('default');
        setLevel(1);
        setNavigationHistory([{ level: 1, id: 'default' }]);
    }

    const closeAndReset = () => {
        resetMultiSheet();
        setMSOpen(false);
    }

    return ({
        maxLevel,
        currentLevel: level,
        open: msOpen,
        developmentUrl,
        setOpen: setMSOpen,
        onClose: () => {
            closeAndReset();
        },
        closeModal: () => {
            onClose && onClose(sheetId);
            closeMultiSheetModal();
            resetMultiSheet();
        },
        previousLevel: goBack,
        onApplySheet: (e) => {
            onApply && onApply(e);
            closeAndReset();
        },
        navigate: openSheet,
        sheetId,
        history: sheetHistory
    })
}

export default useMultiSheet